<script lang="ts">
const POLLING_INTERVAL = 1;
</script>

<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue';
import * as cols from '@/modules/common/components/ag-table/columns/marketplace-depth-of-book';
import { AgTableClient } from '@/modules/common/components/ag-table';
import { DepthOfBookItem, DepthOfBookResponse } from '@/modules/marketplace/models';
import { useRateTypeStore } from '@/store/rate-type-store';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';
import { useInterval } from '@/composables/native';

const props = withDefaults(defineProps<{ cusip: string; asBroker?: boolean }>(), {
  asBroker: false,
});

const marketplaceApiService = useMarketplaceApiService();
const rateTypeStore = useRateTypeStore();

const depthOfBook = shallowRef<DepthOfBookResponse | null>(null);

async function fetch(): Promise<void> {
  depthOfBook.value = props.asBroker
    ? await marketplaceApiService.fetchAdminDepthOfBook(props.cusip)
    : await marketplaceApiService.fetchDepthOfBook(props.cusip);
}

useInterval(fetch, POLLING_INTERVAL * 1000, { immediate: true });

watch(
  () => props.cusip,
  () => {
    if (!props.cusip) {
      // the route that triggers the parent page could still be
      // redirecting between the route without cusip and the one with cusip
      return;
    }

    // we need to force a table re-render when cusip changes
    depthOfBook.value = null;

    void fetch();
  }
);

const columnDefs = computed(() => {
  const rateConfig = { rateType: rateTypeStore.marketplaceRateType };
  return [
    cols.borrowOrderType(),
    cols.borrowCounterparty(),
    cols.borrowQuantity(),
    cols.borrowRate(rateConfig),
    cols.lendRate(rateConfig),
    cols.lendQuantity(),
    cols.lendCounterparty(),
    cols.lendOrderType(),
  ];
});

const items = computed(() => depthOfBook.value?.data ?? []);

function getRowId(item: DepthOfBookItem) {
  return item.idx.toString();
}
</script>

<template>
  <AgTableClient
    v-if="items.length"
    :column-defs="columnDefs"
    fit-full-width
    :get-row-id="getRowId"
    :page-size="100"
    :row-data="items"
    table-id="DepthOfBook"
  />
</template>

<style lang="scss" scoped>
::v-deep {
  .highlighted-border-right {
    border-right: 1px solid #ccc !important; /* @TODO: fix */
  }

  .ag-header {
    color: rgba(255, 255, 255, 0.7);
  }
}
</style>
